import React, { useEffect, useState, useRef, Fragment } from 'react';
import { profileUrl } from 'endpoints';
import { useDispatch, useSelector } from 'react-redux';
import { COMPANY_STORAGE_KEY, INTEGRATION_TYPE } from '../../../../constants';
import { CSSTransition } from 'react-transition-group';
import { LocaleString, getString } from '@nackle/intl-tools';
import { getAvatarLogoColor } from 'utils/color';
import { PointEntry } from './point-entry-input';
import { DownArrow } from 'app/components/shared/icons/icons';
import { Spinner } from 'app/components/shared/spinner/spinner';
import BudgetMeter from 'app/components/shared/budget-meter/budget-meter';
import {
    Avatar,
    Card,
    Col,
    Checkbox,
    List,
    message,
    Radio,
    Row,
    Select
} from '@nackle/paper';
import { clearErrors, getErrors, getReviewLoading } from 'app/app-dux/app-dux';
import { setVisibility } from 'app/app-dux/message-dux';
import {
    setSelectedProgram,
    setSelectedBehavior,
    setDifferentPoints,
    setPointsForPerson,
    getPrograms,
    getSelectedProgram,
    getSelectedBehavior,
    getAwardValue,
    getRemainingBudget,
    getRemainingBudgetPercent,
    getOriginalBudget,
    getTotalAward,
    setBudgetError,
    getBudgetError
} from 'app/app-dux/program-dux';
import { submitRecognition } from 'app/app-dux/recognition-dux';
import {
    getSelectedPeople,
    setIneligibleRecipients,
    getIneligibleRecipients
} from 'app/app-dux/people-dux';

import './program.less';

export function Program( props ) {
    const companyId = localStorage.getItem( COMPANY_STORAGE_KEY );

    const inputsRef = useRef( [] );
    const dispatch = useDispatch();

    const [ animate, setAnimate ] = useState( false );
    const [ invalidPoints, setInvalidPoints ] = useState( {} );


    const programs = useSelector( getPrograms );
    const selectedProgram = useSelector( getSelectedProgram );
    const selectedBehavior = useSelector( getSelectedBehavior );
    const selectedRecipients = useSelector( getSelectedPeople );
    const differentPoints = useSelector( state => state.program.differentPoints );
    const awardValue = useSelector( getAwardValue );
    const originalBudget = useSelector( getOriginalBudget );
    const totalAward = useSelector( getTotalAward );
    const remainingBudget = useSelector( getRemainingBudget );
    const remainingBudgetPercent = useSelector( getRemainingBudgetPercent );
    const budgetError = useSelector( getBudgetError );
    const errors = useSelector( getErrors );
    const ineligibleRecipients = useSelector( getIneligibleRecipients );
    const reviewLoading = useSelector( getReviewLoading );

    useEffect( () => {
        setAnimate( true );
    }, [] );

    useEffect( () => {
        const ineligibleRecipientsList = [];
        errors.map( error => {
            if ( error.personId && error.field !== 'senderPersonId' && error.selectionType === 'MESSAGE' ) {
                ineligibleRecipientsList.push( error.personId );
            }
        } );
        if ( ineligibleRecipientsList.length ) {
            dispatch( setIneligibleRecipients( ineligibleRecipientsList ) );
        }
    }, [ errors.length ] );

    const programSelect = ( selectedProgramId ) => {
        const selectedProgram = JSON.parse( JSON.stringify( programs ) ).find( a => a.rewardChoiceId === selectedProgramId );

        // set to initial state when different program is selected
        setInvalidPoints( {} );
        dispatch( clearErrors() );
        dispatch( setBudgetError( null ) );
        dispatch( setIneligibleRecipients( [] ) );
        dispatch( setSelectedBehavior( {} ) );
        dispatch( setPointsForPerson( 'all', 0 ) );
        dispatch( setDifferentPoints( false ) );

        dispatch( setSelectedProgram( selectedProgram ) );
        dispatch( setVisibility( selectedProgram?.visibility[ 0 ]?.toLowerCase() ) );
        try {
            const results = dispatch( submitRecognition( INTEGRATION_TYPE, 'VALIDATED' ) );
            if ( !results.errors || results.errors.length === 0 )
            {
                if ( selectedProgram.isFixed )
                {
                    dispatch( setPointsForPerson( 'all', selectedProgram.maxAward ) );
                }
                if ( selectedProgram.pointsRequired &&
                    ( !selectedProgram.budgets || selectedProgram.budgets.length === 0 ) ||
                    ( selectedProgram.budgets[ 0 ].convertedPointValue === 0 ) )
                {
                    dispatch( setBudgetError( getString( 'recognition.error.noBudget' ) ) );
                }
            }
        } catch ( err ) {
            // TODO : Need to show any error?
            console.error( err );
        }
    };

    const selectBehavior = ( selectedBehaviorId ) => {
        const selectedBehavior = JSON.parse( JSON.stringify( selectedProgram.behaviors ) ).find( a => a.id === selectedBehaviorId );
        dispatch( setSelectedBehavior( selectedBehavior ) );
    };

    const handleNumberInputChange = ( value, recipientId ) => {
        dispatch( setPointsForPerson( recipientId, value ) );
    };

    const handleDifferentPoints = ( event ) => {
        dispatch( setDifferentPoints( event.target.checked ) );
    };

    const validatePointsRange = ( event ) => {
        const pointsToValidate = event.target.value || event;
        const name = event.target.name;
        if ( pointsToValidate !== '' && pointsToValidate > 0 && ( pointsToValidate < selectedProgram.minAward || pointsToValidate > selectedProgram.maxAward ) ) {
            inputsRef.current[ `${ name }` ].focus();
            setInvalidPoints( { ...invalidPoints, [ name ]: true } );
            message.error( getString( 'recognition.error.outOfRange' ) );
            return false;
        } else {
            setInvalidPoints( { ...invalidPoints, [ name ]: false } );
            return true;
        }
    };

    return (
        <CSSTransition
            timeout={ 1000 }
            in={ animate }
            classNames={ props.forward ? 'slide-in' : 'slide-out' }>
            <div className="program-container spacer-lr-16">
                <div className='program-header'>
                    <LocaleString code="recognition.program.header" TextComponent="h3" className='header-text-bold' />
                    <h4 className='header-text'>
                        <LocaleString code='recognition.program.selectProgramLabel'/>
                        <span className="header-helper-text" >
                            <LocaleString code='recognition.header.required'/>
                        </span>
                    </h4>
                </div>
                <Radio.Group value={ selectedProgram.rewardChoiceId } className="program-selection-card">
                    { programs?.map( program => {
                        return (
                            <div key={ program.rewardChoiceId }>
                                <Card onClick={ () => programSelect( program.rewardChoiceId ) } className="program-container-card">
                                    <div className="program-container-card-details">
                                        <div className='program-top-card'>
                                            <div className='program-info'>
                                                <div className='program-name'>
                                                    <span className="program-name">{ program.rewardChoiceName }</span>
                                                </div>
                                            </div>
                                            <div className='program-select'>
                                                { reviewLoading && selectedProgram.rewardChoiceId === program.rewardChoiceId ?
                                                    <Spinner loading={ reviewLoading }/>
                                                    : <Radio value={ program.rewardChoiceId }/>
                                                }
                                            </div>
                                        </div>
                                        <div className='divider' />
                                        <div className='program-bottom-card'>
                                            {
                                                program.hasPoints && program.budgets && program.budgets.length > 0 &&
                                                <span className="rewards-text-container">
                                                    <LocaleString code="recognition.program.availablePoints" />
                                                    <span className="points-available">
                                                        { program.budgets[ 0 ].convertedPointValue } <LocaleString code="mediaLabel" />
                                                    </span>
                                                    { program.budgets[ 0 ].endDays &&
                                                    <LocaleString code="recognition.program.expiresText" replaceOptions={ { expireDays: program.budgets[ 0 ].endDays } } className="expires-label" />
                                                    }
                                                </span>
                                            }
                                        </div>
                                    </div>
                                    {
                                        ineligibleRecipients && ineligibleRecipients.length > 0 && selectedProgram.rewardChoiceId === program.rewardChoiceId &&
                                        <div className='program-container-error-item'>
                                            { `${ ineligibleRecipients.length } ${ ineligibleRecipients.length > 1
                                                ? getString( 'recognition.program.ineligibleMulti' )
                                                : getString( 'recognition.program.ineligibleSingle' ) }` }
                                        </div>
                                    }
                                    { selectedProgram.rewardChoiceId === program.rewardChoiceId && budgetError &&
                                    <div className='program-container-error-item'>
                                        { budgetError }
                                    </div>
                                    }
                                </Card>
                            </div>
                        );
                    } ) }
                </Radio.Group>
                <Row>
                    <Col span={ 24 }>
                        { selectedProgram.rewardChoiceId && selectedProgram?.behaviors && selectedProgram.behaviors.length > 0 &&
                        <Fragment>
                            <div className="behavior-header">
                                <h4 className='header-text'>
                                    <LocaleString code='recognition.program.behaviorHeader'/>
                                    <span className="header-helper-text">
                                        { selectedProgram.isBehaviorRequired ? getString( 'recognition.header.required' ) : getString( 'recognition.header.optional' ) }
                                    </span>
                                </h4>
                                <Select
                                    size="large"
                                    style={ { width: '100%' } }
                                    onChange={ selectBehavior }
                                    className="behavior-select"
                                    suffixIcon={ <DownArrow /> }
                                    value={ selectedBehavior?.id }
                                    placeholder={ getString( 'recognition.program.behaviorSelectPlaceholder' ) }>
                                    {
                                        selectedProgram.behaviors.map( behavior => {
                                            return (
                                                <Select.Option key={ behavior.id }>
                                                    <Avatar className="behavior-image" src={ behavior.imageUrl }/>
                                                    { behavior.name }
                                                </Select.Option>
                                            );
                                        } )
                                    }
                                </Select>
                            </div>
                        </Fragment>
                        }
                    </Col>
                </Row>
                {
                    selectedProgram?.hasPoints && selectedProgram?.budgets?.length > 0 &&
                    <div className="points-container">
                        <Row>
                            <Col span={ 24 }>
                                <h4 className='header-text'>
                                    <LocaleString code='recognition.program.pointsHeader'/>
                                    <span className="header-helper-text">
                                            { !selectedProgram.pointsRequired
                                                ? getString( 'recognition.header.optional' )
                                                : getString( 'recognition.header.required' )
                                            }
                                        </span>
                                </h4>
                            </Col>
                        </Row>

                        { remainingBudget < 0 &&
                        <div className='error-text'>
                            <LocaleString code='recognition.error.tooManyPoints' />
                        </div>
                        }

                        { selectedRecipients.length > 1 && !selectedProgram.isFixed &&
                        <Row>
                            <Col span={ 24 }>
                                <Checkbox
                                    className="points-checkbox"
                                    onChange={ handleDifferentPoints }
                                    checked={ differentPoints }>
                                    <LocaleString code="recognition.program.differentPoints" />
                                </Checkbox>
                            </Col>
                        </Row>
                        }
                        <Row>
                            <Col span={ 8 }>
                                {
                                    !differentPoints &&
                                    <PointEntry
                                        name="allPoints"
                                        ariaLabel={ getString( 'recognition.allPointsInput.label' ) }
                                        inputRef={ input => { inputsRef.current[ 'allPoints' ] = input; } }
                                        invalidPoints={ invalidPoints }
                                        onChange={ ( value ) => handleNumberInputChange( value, 'all' ) }
                                        onFocus={ ( e ) => e.target.value === 0 ? handleNumberInputChange( selectedProgram.isFixed ? selectedProgram.maxAward : '', 'all' ) : null }
                                        onBlur={ ( value ) => validatePointsRange( value, 'all' ) }
                                        value={ awardValue }
                                        placeholder={ getString( 'mediaLabel' ) }
                                        disabled={ ( selectedProgram.isFixed && selectedProgram.pointsRequired ) || remainingBudget === 0 }
                                    />
                                }
                            </Col>
                        </Row>

                        { !selectedProgram.isFixed &&
                        <Row>
                            <Col span={ 24 } className="range-helper-text">
                                <LocaleString
                                    code= { differentPoints ? 'recognition.program.rangePerPersonInstructions' : 'recognition.program.rangeInstructions' }
                                    replaceOptions={ { min: selectedProgram.minAward, max: selectedProgram.maxAward } }
                                />
                            </Col>
                        </Row>
                        }

                        <Row justify="center">
                            <Col span={ 24 }>
                                { differentPoints &&
                                <div className="scroll-container">
                                    <List
                                        size="small"
                                        dataSource={ selectedRecipients }
                                        renderItem={ recipient => (
                                            <List.Item className="flex-item" key={ recipient.id }>
                                                <List.Item.Meta
                                                    avatar={
                                                        <Avatar
                                                            style={ getAvatarLogoColor( recipient ) }
                                                            src={ `${ profileUrl }/public/images/image/${ companyId }/${ recipient.id }/avatar` }
                                                        >
                                                            { recipient.givenName.slice( 0, 1 ) }
                                                        </Avatar>
                                                    }
                                                    title={ `${ recipient.givenName } ${ recipient.surname }` }
                                                />
                                                <PointEntry
                                                    name={ `eachPoints_${ recipient.id }` }
                                                    inputRef={ input => { inputsRef.current[ `eachPoints_${ recipient.id }` ] = input; } }
                                                    ariaLabel={ getString( 'recognition.individualPointsInput.label', { name: `${ recipient.givenName } ${ recipient.surname }` } ) }
                                                    onChange={ ( value ) => handleNumberInputChange( value, recipient.id ) }
                                                    onFocus={ ( e ) =>  e.target.value === 0 ? handleNumberInputChange( '', recipient.id ) : null }
                                                    onBlur={ ( e ) => validatePointsRange( e, recipient.id ) }
                                                    value={ recipient.awardValue }
                                                    invalidPoints={ invalidPoints }
                                                    disabled={ selectedProgram.isFixed && selectedProgram.pointsRequired }
                                                />
                                            </List.Item>
                                        ) }
                                    >
                                    </List>
                                </div>
                                }
                                <Row className="points-progress">
                                    <Col span={ 24 }>
                                        <BudgetMeter
                                            totalAward={ totalAward }
                                            originalBudget={ originalBudget }
                                            percent={ remainingBudgetPercent }
                                            current={ remainingBudget }
                                        />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </div>
                }
            </div>
        </CSSTransition>
    );
}